/**
 * KundenfreigabeZustellenDialog — «Dem Kunden zustellen» (P2 Placement Slice 12, Tab 4c). Radio for
 * the channel (preselected from the Kanalsteuerung), an optional Hinweis, the FIDLEG note.
 */

import { useState } from 'react';
import {
  KUNDENFREIGABE_FIDLEG_HINWEIS,
  KUNDENFREIGABE_KEIN_PORTAL_HINWEIS,
  KUNDENFREIGABE_KEINE_EMAIL_HINWEIS,
  KUNDENFREIGABE_NICHT_FREIGEGEBEN_HINWEIS,
  KUNDENFREIGABE_VERALTET_HINWEIS,
  KUNDENFREIGABE_OFFEN_HINWEIS,
  type CustomerDeliveryChannel,
  type KundenfreigabeResponse,
  type KundenfreigabeZustellenBody,
} from '@brokly/shared';
import { dateShortDe } from '../../lib/format';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';

const ERROR_TEXT: Record<string, string> = {
  versand_unterbrochen: 'Versand unterbrochen - bitte zuerst aufloesen',
  record_failed: 'E-Mail gesendet, aber nicht erfasst. Bitte in der Zustellung aufloesen.',
  portal_zugang_fehlt: KUNDENFREIGABE_KEIN_PORTAL_HINWEIS,
  kunde_email_fehlt: KUNDENFREIGABE_KEINE_EMAIL_HINWEIS,
  recommendation_not_released: KUNDENFREIGABE_NICHT_FREIGEGEBEN_HINWEIS,
  report_veraltet: KUNDENFREIGABE_VERALTET_HINWEIS,
  zustellung_offen: KUNDENFREIGABE_OFFEN_HINWEIS,
  mailbox_not_connected: 'Keine Mailbox verbunden - bitte zuerst unter Einstellungen verbinden',
  communication_send_required: 'Keine Berechtigung zum Versand von E-Mails',
  versand_laeuft: 'Versand laeuft - bitte warten',
};

export function KundenfreigabeZustellenDialog({
  open,
  onClose,
  facts,
  onZustellen,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: KundenfreigabeResponse;
  onZustellen: (body: KundenfreigabeZustellenBody) => void;
  saving: boolean;
  error: { code?: string } | null;
}) {
  const [channel, setChannel] = useState<CustomerDeliveryChannel>(facts.kanal_vorschlag);
  const [hinweis, setHinweis] = useState('');

  const errorText = error?.code ? (ERROR_TEXT[error.code] ?? 'Zustellung fehlgeschlagen') : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dem Kunden zustellen"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            disabled={saving || (channel === 'portal' && !facts.portal_verfuegbar) || (channel === 'email' && facts.kunde_email === null)}
            onClick={() => onZustellen({ channel, hinweis: hinweis.trim() ? hinweis.trim() : null })}
            data-testid="kundenfreigabe-zustellen-submit"
          >
            Zustellen
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm" data-testid="kundenfreigabe-zustellen-dialog">
        {facts.freigabe && (
          <p className="text-muted-foreground">
            Empfehlung: {facts.freigabe.empfehlung_carrier_name} (Lauf {facts.freigabe.lauf_nr}) - freigegeben am{' '}
            {dateShortDe(facts.freigabe.freigegeben_at)} von {facts.freigabe.freigegeben_by_name ?? '-'}
          </p>
        )}
        <fieldset className="space-y-2">
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="kf-channel"
              value="portal"
              checked={channel === 'portal'}
              disabled={!facts.portal_verfuegbar}
              onChange={() => setChannel('portal')}
              data-testid="kf-channel-portal"
            />
            <span>
              Kundenportal
              {facts.kanal_vorschlag === 'portal' && <span className="ml-2 text-xs text-muted-foreground">(gemaess Kanalsteuerung)</span>}
              {!facts.portal_verfuegbar && <span className="block text-xs text-muted-foreground">{KUNDENFREIGABE_KEIN_PORTAL_HINWEIS}</span>}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="kf-channel"
              value="email"
              checked={channel === 'email'}
              disabled={facts.kunde_email === null}
              onChange={() => setChannel('email')}
              data-testid="kf-channel-email"
            />
            <span>
              E-Mail an {facts.kunde_email ?? '-'}
              {facts.kanal_vorschlag === 'email' && <span className="ml-2 text-xs text-muted-foreground">(gemaess Kanalsteuerung)</span>}
              {facts.kunde_email === null && <span className="block text-xs text-muted-foreground">{KUNDENFREIGABE_KEINE_EMAIL_HINWEIS}</span>}
            </span>
          </label>
        </fieldset>
        {facts.kanal_hinweis && <p className="text-xs text-muted-foreground">{facts.kanal_hinweis}</p>}
        <label className="block">
          <span className="text-xs text-muted-foreground">Hinweis fuer die Historie (optional)</span>
          <textarea
            className="mt-1 w-full rounded-md border border-border p-2"
            maxLength={2000}
            value={hinweis}
            onChange={(e) => setHinweis(e.target.value)}
            data-testid="kf-hinweis"
          />
        </label>
        <p role="note" className="text-xs text-muted-foreground">{KUNDENFREIGABE_FIDLEG_HINWEIS}</p>
        {errorText && <p className="text-sm text-red-600" data-testid="kf-zustellen-error">{errorText}</p>}
      </div>
    </Dialog>
  );
}
